var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Input_1;
import React, { createRef, PureComponent } from 'react';
import { AutocompleteType, InputType, Placement, SensitiveData, TextInputCapitalize } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, element, eventListener, expression, formatters, number, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent, FocusEvent, KeyboardEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { validators } from '@philips-internal/oswald-ui-model';
import { classNames, defaultClasses, enumValues, isContentfulString, isDefined, isEmptyObject, isEnumValid, isFunction, noop, trigger } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as HideIcon } from '../../../resources/assets/components/DLS_Hide.svg';
import { ReactComponent as ShowIcon } from '../../../resources/assets/components/DLS_Show.svg';
import { FieldContext } from '../field/context/field-context.js';
import { Text } from '../text/text.js';
import { InputAPI } from './input-api.js';
import { InputMode } from './input-constants.js';
import { InputFormatter } from './input-formatter.js';
import './input.css';
function InputCounter(params) {
    const { length, value = '' } = params;
    if (!length && length !== 0) {
        return null;
    }
    const valueLength = value ? String(value).length : 0;
    const counterClasses = {
        'c-input__counter': true,
        'u-color-safety-red': length - valueLength < 0,
    };
    const count = `(${valueLength}/${length})`;
    return React.createElement("span", { className: classNames(counterClasses) }, count);
}
InputCounter.propTypes = {
    length: number,
    value: string,
};
let Input = Input_1 = class Input extends PureComponent {
    constructor() {
        super(...arguments);
        this.inputRef = createRef();
        this.state = {
            showPassword: false,
        };
        this.getInputProps = () => {
            const propsToIgnore = [
                'autoSelect',
                'children',
                'externalAction',
                'externalActionPlacement',
                'onChange',
                'onKeyDown',
                'onKeyPress',
                'onKeyUp',
                'showLength',
                'showPasswordToggle',
                'suppressValidations',
                'size',
                'valid',
            ];
            const propNames = Object.keys(Input_1.propTypes).filter((propName) => !propsToIgnore.includes(propName));
            return propNames.reduce((props, propName) => {
                if (propName in this.props) {
                    //@ts-ignore ignoring because TS keeps messing with the properties index type even with type declared
                    props[propName] = this.props[propName];
                }
                return props;
            }, {});
        };
        this.normalizeProps = (props) => {
            const { showPassword } = this.state;
            const { autoComplete, autoCorrect, enabled, placeholder, sensitiveData = Input_1.defaultProps.sensitiveData, ...otherProps } = props;
            const normalizedProps = { ...otherProps, sensitiveData };
            if ('autoComplete' in props) {
                // We use it with the capitalized "C" because React requires camelCase for DOM properties, but outputs the correct ones in the end.
                // https://github.com/facebook/react/issues/2882#issuecomment-70931554
                // There seem to be controversies about how to disable autofill/autocomplete on chrome since "off" is being ignored,
                // for now, the most reliable way I found is to provide an invalid key (like "none"), which in turn disables both autofill/autocomplete since there's no history match.
                const isBoolean = (autoComplete) => typeof autoComplete === 'boolean';
                const getValueOfOldAutoComplete = (autoComplete) => autoComplete ? AutocompleteType.ON : AutocompleteType.NONE;
                if (autoComplete) {
                    normalizedProps.autoComplete = isBoolean(autoComplete) ? getValueOfOldAutoComplete(autoComplete) : autoComplete;
                }
            }
            if ('autoCorrect' in props) {
                normalizedProps.autoCorrect = autoCorrect ? AutocompleteType.ON : AutocompleteType.OFF;
            }
            if ('enabled' in props) {
                normalizedProps.disabled = !enabled;
            }
            if ('placeholder' in props) {
                normalizedProps.placeholder = MessageService.translate(placeholder);
            }
            if (props.type === InputType.PASSWORD && showPassword) {
                normalizedProps.type = InputType.TEXT;
            }
            if (props.type === InputType.NUMBER && !isEmptyObject(this.getValueFormatter())) {
                normalizedProps.type = InputType.TEXT;
            }
            return normalizedProps;
        };
        this.getValueFormatter = () => {
            const { formatters } = this.props;
            return formatters?.value;
        };
        this.getKeyEventObject = (event) => {
            const { value: oldValue } = this.props;
            const { altKey, cancelable, charCode, ctrlKey, currentTarget, key, keyCode, preventDefault, shiftKey, stopPropagation } = event;
            const { value } = currentTarget;
            return {
                altKey,
                cancelable,
                charCode,
                ctrlKey,
                key,
                keyCode,
                oldValue,
                preventDefault,
                shiftKey,
                stopImmediatePropagation: noop,
                stopPropagation,
                value,
            };
        };
        this.onChange = (event) => {
            const { prettyValue, value } = event;
            const { onChange, value: oldValue } = this.props;
            if (!isFunction(onChange) || !this.isLengthValid(value)) {
                return;
            }
            this.updateValidations(value);
            onChange(new ChangeEvent({ oldValue: String(oldValue), prettyValue, value }));
        };
        this.onKeyboardEvent = (event, eventCallback) => {
            const params = this.getKeyEventObject(event);
            if (!isFunction(eventCallback)) {
                return;
            }
            eventCallback(new KeyboardEvent(params));
        };
        this.onKeyDown = (event) => {
            this.onKeyboardEvent(event, this.props.onKeyDown);
        };
        this.onKeyPress = (event) => {
            this.onKeyboardEvent(event, this.props.onKeyPress);
        };
        this.onKeyUp = (event) => {
            this.onKeyboardEvent(event, this.props.onKeyUp);
        };
        this.updateValidations = (value) => {
            if (!this.context || this.props.suppressValidations) {
                return;
            }
            const { max, min } = this.props;
            const validationRunner = ModelService.validate(value);
            const valueFormatter = this.getValueFormatter();
            if (isDefined(valueFormatter)) {
                validationRunner.apply(valueFormatter.name, valueFormatter.options);
            }
            if (isDefined(max)) {
                validationRunner.apply(validators.MAX_VALUE, { max });
            }
            if (isDefined(min)) {
                validationRunner.apply(validators.MIN_VALUE, { min });
            }
            const validations = validationRunner.run();
            this.context.updateChildValidations(validations);
        };
        this.renderInput = () => {
            const { onBlur, onFocus, value, length, enforceLength, ellipsis, readOnly, sensitiveData, ...props } = this.normalizeProps(this.getInputProps());
            const formatterValue = this.getValueFormatter();
            const formatters = isEmptyObject(formatterValue) ? {} : { value: formatterValue };
            /**
             * Rendering a Text here because input tag do not works well with ellipsis nor tooltip.
             */
            if (readOnly) {
                const sensitivity = props.type === InputType.PASSWORD ? SensitiveData.HIDDEN : sensitiveData;
                return (React.createElement(Text, { className: "c-input__text", disableTranslation: true, ellipsis: ellipsis, formatters: formatters, sensitiveData: sensitivity, value: String(value) }));
            }
            return (React.createElement(InputFormatter, { formatter: this.getValueFormatter(), onBlur: trigger(onBlur, new FocusEvent()), onChange: this.onChange, onFocus: trigger(onFocus, new FocusEvent()), onKeyDown: this.onKeyDown, onKeyPress: this.onKeyPress, onKeyUp: this.onKeyUp, readOnly: readOnly, ref: this.inputRef, sensitiveData: sensitiveData, value: String(value) },
                React.createElement("input", { ...props, autoFocus: props.autoFocus, className: "c-input__input", "data-testid": "input-element", maxLength: enforceLength ? length : undefined, readOnly: readOnly, tabIndex: 0 })));
        };
        this.onToggleClick = () => {
            this.setState((prevState) => {
                return {
                    showPassword: !prevState.showPassword,
                };
            });
        };
        this.renderPasswordToggle = () => {
            const { showPassword } = this.state;
            const { enabled, readOnly, showPasswordToggle, type } = this.props;
            if (type !== InputType.PASSWORD || !showPasswordToggle || !enabled || readOnly) {
                return null;
            }
            return (React.createElement("div", { className: "c-input__toggle-password u-hide@view u-hide@empty", onClick: this.onToggleClick }, showPassword ? React.createElement(HideIcon, null) : React.createElement(ShowIcon, null)));
        };
        this.renderExternalAction = () => {
            const { externalAction, externalActionPlacement } = this.props;
            if (!externalAction) {
                return null;
            }
            const externalActionClassname = classNames({
                'c-input__action': true,
                'u-hide@empty': true,
                'u-hide@view': true,
                [`c-input__action--${externalActionPlacement}`]: isEnumValid(Placement, externalActionPlacement),
            });
            return (React.createElement("div", { className: externalActionClassname, "x-contrast": "bg" }, externalAction));
        };
        this.renderFooter = () => {
            const { enabled, length, readOnly, showLength, value } = this.props;
            if (!length || !enabled || readOnly || !showLength) {
                return null;
            }
            return (React.createElement("div", { className: "c-input__footer" },
                React.createElement(InputCounter, { length: length, value: value })));
        };
    }
    /**
     * Prettifies a given value according to formatters.
     *
     * @param {object} properties - Properties to generate pretty value.
     * @param {Formatters | undefined} [properties.formatters] - Formatters used to format the value.
     * @param {string | number} [properties.value] - Value to be formatted.
     * @returns {string} pretty value.
     */
    static formatPrettyValue(params) {
        const { formatters, value } = params;
        const valueFormatter = formatters?.value;
        if (!isContentfulString(value) && typeof value !== 'number') {
            return '';
        }
        return InputFormatter.formatPrettyValue({ formatter: valueFormatter, value });
    }
    isLengthValid(value) {
        const { enforceLength, length } = this.props;
        return !length || !enforceLength || String(value).length <= length;
    }
    componentDidMount() {
        const { autoSelect } = this.props;
        if (autoSelect) {
            this.selectInput();
        }
        this.updateValidations(this.props.value);
    }
    componentDidUpdate(prevProps) {
        const { focused } = this.props;
        if (prevProps.focused !== focused && focused) {
            this.focusInput();
        }
    }
    componentWillUnmount() {
        if (!this.props.suppressValidations) {
            this.context?.resetChildValidations();
        }
    }
    focusInput() {
        const { autoSelect } = this.props;
        this.inputRef.current?.focus();
        if (autoSelect) {
            this.selectInput();
        }
    }
    selectInput() {
        this.inputRef.current?.select();
    }
    render() {
        const { className, ellipsis, enabled, externalActionPlacement, readOnly, valid } = this.props;
        const defClasses = defaultClasses({ enabled: Boolean(enabled), readOnly: Boolean(readOnly), valid: Boolean(valid) });
        const inputClassName = classNames({
            'c-input': true,
            'c-input--ellipsis': Boolean(ellipsis),
            'c-input--validated': !valid,
            ...defClasses,
            [`${className}`]: Boolean(className),
        });
        return (React.createElement("div", { className: inputClassName, "data-testid": "input" },
            React.createElement("div", { className: "c-input__box" },
                externalActionPlacement === Placement.LEFT && this.renderExternalAction(),
                this.renderInput(),
                this.renderPasswordToggle(),
                externalActionPlacement === Placement.RIGHT && this.renderExternalAction()),
            this.renderFooter()));
    }
};
Input.contextType = FieldContext;
Input.propTypes = {
    autoCapitalize: oneOf(enumValues(TextInputCapitalize)),
    autoComplete: oneOf(enumValues(AutocompleteType)),
    autoCorrect: bool,
    autoFocus: bool,
    autoSelect: bool,
    className: string,
    ellipsis: bool,
    enabled: bool,
    enforceLength: bool,
    externalAction: element.notEnumerable(),
    externalActionPlacement: oneOf([Placement.LEFT, Placement.RIGHT]),
    focused: bool,
    formatters: formatters,
    inputMode: oneOf(enumValues(InputMode)),
    length: number,
    max: number,
    min: number,
    name: string,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    onKeyDown: eventListener,
    onKeyPress: eventListener,
    onKeyUp: eventListener,
    placeholder: expression,
    readOnly: bool,
    role: string,
    sensitiveData: oneOf(enumValues(SensitiveData)),
    showLength: bool,
    showPasswordToggle: bool,
    step: number,
    suppressValidations: bool,
    tabIndex: number,
    type: oneOf(enumValues(InputType)),
    valid: bool,
    value: oneOfType([number, string]),
};
Input.defaultProps = {
    autoCapitalize: TextInputCapitalize.NONE,
    enabled: true,
    enforceLength: true,
    externalActionPlacement: Placement.RIGHT,
    formatters: {},
    inputMode: InputMode.TEXT,
    sensitiveData: SensitiveData.NONE,
    showLength: true,
    showPasswordToggle: true,
    type: InputType.TEXT,
    valid: true,
    value: '',
};
Input = Input_1 = __decorate([
    AwesomeComponent({ api: InputAPI, type: ComponentType.DATA_ENTRY })
], Input);
export { Input, };
//# sourceMappingURL=input.js.map